import React, { type JSX } from 'react'
import styled from '@emotion/styled'
import { isErrored, isLoading } from '@rwwa/rwwa-data-access'
import dayjs from 'dayjs'
import type { Race } from '@mobi/betslip/types'
import { Grid, GridCell } from '@mobi/component-library/Legacy/Grid'
import { Icon } from '@mobi/component-library/Legacy/Icon'
import { hexColors } from '@mobi/settings'
import { connect } from '@core/Components/HOCs/connect'
import { ListTime } from '@core/Components/List'
import { ListLink } from '@core/Components/List/ListLink'
import { ErrorMessage } from '@core/Components/Messages'
import { Money } from '@core/Components/Text/Money'
// separate import here to fix tests TODO: dont do this
import {
  FavouriteNumberPool,
  Pools,
  Races,
  RacingPool,
  TippingPool,
  isFavouriteNumbersPool,
  isRacingPool,
  isTippingPool,
} from '@core/Data/Racing'
import { JackpotTextStyled, JackpotTitleStyled } from './Jackpot.styles'
import { JackpotLoading } from './JackpotLoading'

const ErroredJackpot = () => {
  return <ErrorMessage>Error loading jackpot</ErrorMessage>
}

export const Jackpot = connect({ pool: Pools })(({ pool }) => {
  if (isLoading(pool)) {
    return <JackpotLoading />
  }

  if (isErrored(pool)) {
    return <ErroredJackpot />
  }

  if (isRacingPool(pool)) {
    return <JackpotWithRace loadedPool={pool} race={pool.raceKeys[0]} />
  }

  return <LoadedJackpot pool={pool} />
})

const JackpotWithRace = connect({ race: Races })<{ loadedPool: RacingPool }>(({
  loadedPool,
  race,
}) => {
  if (isLoading(race)) {
    return <JackpotLoading />
  }

  if (isErrored(race)) {
    return <ErroredJackpot />
  }

  return <LoadedJackpot pool={loadedPool} race={race} />
})

export const LoadedJackpot = ({
  pool,
  race,
}: {
  pool: TippingPool | RacingPool | FavouriteNumberPool
  race?: Race
}): JSX.Element => {
  if (isRacingPool(pool) && !!race) {
    let codeIcon

    switch (pool.fixtureType) {
      case 'Races':
        codeIcon = <Icon type='races' />
        break
      case 'Dogs':
        codeIcon = <Icon type='dog' />
        break
      case 'Trots':
        codeIcon = <Icon type='trot' />
        break
      default:
        return <ErroredJackpot />
    }

    return (
      <JackpotContent
        title={race.meetingName}
        subTitle={pool.name}
        endTime={new Date(race.raceTime)}
        prefix={race.meetingCode}
        badgeText={race.raceNumber.toString()}
        href={`/tote/meetings/${race.meetingCode.toLowerCase()}/${race.raceNumber}?date=${dayjs(
          race.meetingDate
        ).format('YYYY-MM-DD')}`}
        icon={codeIcon}
        netJackpotAmount={pool.netJackpotAmount}
      />
    )
  }

  if (isFavouriteNumbersPool(pool)) {
    return (
      <JackpotContent
        title='Favourite Numbers'
        subTitle={pool.name}
        endTime={pool.scheduledCloseDateTime}
        badgeText='FN'
        href='/favourite-numbers'
        icon={<Icon type='favouriteNumbers' />}
        netJackpotAmount={pool.netJackpotAmount}
      />
    )
  }

  if (isTippingPool(pool)) {
    return (
      <JackpotContent
        title={pool.sportDescription}
        subTitle={pool.name}
        endTime={pool.gameStartDateTime}
        badgeText='TIP'
        href={`/totesports/${pool.sportCode.toLowerCase()}/${pool.key}`}
        icon={<Icon type={pool.sportCode} />}
        netJackpotAmount={pool.netJackpotAmount}
      />
    )
  }

  return <ErroredJackpot />
}

function getDisplayEndTime(endTime: Date) {
  const dayjsEndTime = dayjs(endTime)
  return (
    <ListTime>
      {dayjsEndTime.isSame(Date.now(), 'd')
        ? dayjsEndTime.format('HH:mm')
        : dayjsEndTime.format('D MMM')}
    </ListTime>
  )
}

interface JackpotContentProps {
  title: string
  subTitle: string
  endTime?: Date
  prefix?: string
  badgeText: string
  href: string
  icon: JSX.Element
  netJackpotAmount: number
}

const JackpotContent = ({
  title,
  subTitle,
  endTime,
  prefix,
  badgeText,
  href,
  icon,
  netJackpotAmount,
}: JackpotContentProps): JSX.Element => {
  return (
    <ListLink to={href}>
      <Grid>
        <GridCell width='3em' align='center'>
          <ListBadgeStyled>
            <span>{prefix}</span>
            <span>{badgeText}</span>
          </ListBadgeStyled>
          {endTime ? getDisplayEndTime(endTime) : ''}
        </GridCell>
        <GridCell width='3em' align='center'>
          {icon}
        </GridCell>
        <GridCell>
          <JackpotTitleStyled>{title}</JackpotTitleStyled>
          <JackpotTextStyled>{subTitle}</JackpotTextStyled>
          <JackpotTextStyled>
            <Money amount={netJackpotAmount} />
          </JackpotTextStyled>
        </GridCell>
      </Grid>
    </ListLink>
  )
}

// ======
// Styles
// ======

const ListBadgeStyled = styled.div({
  boxSizing: 'border-box',
  display: 'flex',
  flexDirection: 'column',
  width: '4rem',
  height: '4rem',
  padding: '0.5rem',
  color: hexColors.white,
  textAlign: 'center',
  fontSize: '1.4em',
  fontWeight: 'bold',
  textTransform: 'uppercase',
  lineHeight: 1,
  background: hexColors.sushi,

  '> span:nth-of-type(1)': {
    fontSize: '0.5em',
    lineHeight: 1,
  },
})
